/*-------------------------------------------*/
/*----------IMPORTS AND INITIALIZERS---------*/
/*-------------------------------------------*/

// Imports
import { 
    Box, Grid, Skeleton, Table, 
    TableBody, TableCell, 
    TableContainer, TableHead, 
    TablePagination, TableRow,
    Typography
}  from "@mui/material"
import axios from "axios"
import { Fragment, useEffect, useState } from "react"
import { Link } from "react-router-dom"

/*-------------------------------------------*/
/*------------PRE-CONFIG INTERFACES----------*/
/*-------------------------------------------*/

// Interface for defining column headers for table
interface Column {
    id: string
    label: string
    align?: 'left' | 'right' | 'center'
    format?: (value: number) => string
}

// Interface for defining table configuration type
interface KeyValueType {
    [key: string]: any
}

// Part of type definition for props
interface Props {
    open: boolean, drawerWidth: number,
    showSkeleton: boolean, setShowSkeleton: (bool: boolean) => void,
    fetchedFilterRecords: KeyValueType[], setFetchedFilteredRecords: (result: KeyValueType[]) => void
    secondarySearchRecords: KeyValueType[], setSecondarySearchRecords: (result: KeyValueType[]) => void
    recordsToRender: KeyValueType[], setRecordsToRender: (result: KeyValueType[]) => void
    tablePageNumber: boolean, setTablePageNumber: (bool: boolean) => void,
    enterpriseName: string, setEnterpriseName: (name: string) => void,
    renderEntName: boolean, setRenderEntName: (bool: boolean) => void 
}

/*------------------------------------------*/
/*-----------------COMPONENT----------------*/
/*------------------------------------------*/

const TableOfRecords: React.FC<Props> = ({
    open, drawerWidth,
    showSkeleton, setShowSkeleton,
    fetchedFilterRecords, setFetchedFilteredRecords,
    secondarySearchRecords, setSecondarySearchRecords,
    recordsToRender, setRecordsToRender,
    tablePageNumber, setTablePageNumber,
    enterpriseName, setEnterpriseName,
    renderEntName, setRenderEntName
}) => {

    /*--------------------*/
    /*-STATES AND METHODS-*/
    /*--------------------*/

    const [linkRecordClicked, setLinkRecordClicked] = useState<boolean>(false)

    // For table pagination
    const [columns, setColumns] = useState<Column[]>([])

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    // DynamoDb table and browser table column matching
    const columnsFormatted: KeyValueType = {
        'addons': 'Addons',
        'bundle_name': 'Bundle Name',
        'did_number': 'DID Number',
        'ent_name': 'Enterprise Name',
        'suspended_status': 'Status',
        'tmobile_ent_id': 'T-Mobile Enterprise Id',
        'tmobile_number': 'T-Mobile Number'
    }

    // Function to reorder object keys
    const reorderKeys = (obj: KeyValueType, order: string[]): KeyValueType => {
        const reorderedObj: KeyValueType = {};
        order.forEach(key => {
            if (key in obj) {
                reorderedObj[key] = obj[key];
                console.log(reorderedObj[key])
            }
        });
        return reorderedObj;
    }

    const capitalizeWords = (str: string): string => {
        return str
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
    
    // For handling link record click
    const handleLinkRecordClick = (queryArgs: KeyValueType) => {

        setRecordsToRender([])
        setLinkRecordClicked(true)
        setShowSkeleton(true)
        setRenderEntName(true)
        setEnterpriseName(capitalizeWords(fetchedFilterRecords[0].ent_name))
        let url = `https://${process.env.REACT_APP_HOST_NAME}/${process.env.REACT_APP_RETRIEVE_RECORDS_ROUTE}?`
        if (queryArgs['ent_name'] !== undefined) {
            url += `ent_name=${encodeURIComponent(queryArgs['ent_name'])}`
        } else {
            url += `tmobile_ent_id=${encodeURIComponent(queryArgs['tmobile_ent_id'])}`
        }
        url += `&queryType=2`

        axios.get(url)
            .then(res => {
                const parsedResponse: KeyValueType[] = JSON.parse(res.data)
                const order = ["tmobile_number", "did_number", "ent_name", "tmobile_ent_id", "suspended_status", "bundle_name", "addons"]
                const reorderedResponse = parsedResponse.map(obj => reorderKeys(obj, order));
                setSecondarySearchRecords(reorderedResponse)
            })
            .catch(err => console.log(err))
    }

    // For enabling forward chevron icon button based on the if condition
    useEffect(() => {
        if (linkRecordClicked === true) setTablePageNumber(true)
    }, [linkRecordClicked])

    // For setting the appropriate column headers when tablePageNumber state hook changes value
    useEffect(() => {

        if (tablePageNumber === true) {
            setColumns(
                secondarySearchRecords[0] ?
                Object.entries(secondarySearchRecords[0])
                    .map(([key, value]) => ({
                        id: key,
                        label: key.charAt(0).toUpperCase() + key.slice(1),
                        align: 'left',
                        format: (value: number) => value.toLocaleString('en-US')
                    })) : []
            )
            setRecordsToRender(secondarySearchRecords)
        } else {
            setColumns(
                fetchedFilterRecords[0] ?
                Object.entries(fetchedFilterRecords[0])
                    .map(([key, value]) => ({
                        id: key,
                        label: key.charAt(0).toUpperCase() + key.slice(1),
                        align: 'left',
                        format: (value: number) => value.toLocaleString('en-US')
                    })) : []
            )
            setRecordsToRender(fetchedFilterRecords)
        }
    }, [tablePageNumber, fetchedFilterRecords, secondarySearchRecords])

    // Once recordsToRender has been set, disable skeleton loader rendering
    useEffect(() => {
        if (recordsToRender.length !== 0) {
            setShowSkeleton(false)
            setLinkRecordClicked(false)
        }
    }, [recordsToRender])

    // For handling sorting among column values
    const [sortingOrder, setSortingOrder] = useState('ASC')
    const performSort = (label: string) => {

        const sortHelper = (a: any, b: any) => {

            const aVal = a[label.toLowerCase()];
            const bVal = b[label.toLowerCase()];

            let aValue = "", bValue = ""
            if (!(aVal[0] === "[" && aVal[aVal.length-1] === "]")) 
                aValue = !isNaN(Number(aVal)) ? Number(aVal) : aVal.toLowerCase();
            else {
                const aArr = JSON.parse(aVal)
                aValue = JSON.stringify(aArr.sort())
            }

            if (!(bVal[0] === "[" && bVal[bVal.length-1] === "]")) 
                bValue = !isNaN(Number(bVal)) ? Number(bVal) : bVal.toLowerCase();
            else {
                const bArr = JSON.parse(bVal)
                bValue = JSON.stringify(bArr.sort())
            }

            if (aValue > bValue) return 1;
            if (aValue < bValue) return -1;
            return 0;
        };
    
        if (sortingOrder === 'ASC') {

            const sortedFilterRecords = [...recordsToRender].sort(sortHelper);
            setRecordsToRender(sortedFilterRecords);
            setSortingOrder('DSC');

        } else if (sortingOrder === 'DSC') {

            const sortedFilterRecords = [...recordsToRender].sort((a, b) => -sortHelper(a, b));
            setRecordsToRender(sortedFilterRecords);
            setSortingOrder('ASC');
        }
    };

    /*------------------*/
    /*-RENDER COMPONENT-*/
    /*------------------*/

    return (
        <Box>
            {
                recordsToRender.length > 0 ? (
                    <>
                    {
                        renderEntName && enterpriseName !== "" && (
                        <Typography
                            sx={{
                                fontSize: '24px',
                                fontWeight: 'bold',
                                ml: '10px',
                                mb: '10px'
                            }}
                        >
                            {enterpriseName}
                        </Typography>
                    )}
                    {/* Render the table container */}
                    <TableContainer sx={{ maxHeight: 440, maxWidth: open ? `calc(100vw - ${drawerWidth}px)` : `calc(150vw - ${drawerWidth}px)`}}>
                        <Table stickyHeader aria-label="sticky table">

                            {/* Render the table columns */}
                            <TableHead>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        style={{ 
                                            cursor: 'pointer', 
                                            fontWeight: 'bold',
                                            color: 'white',
                                            backgroundColor: '#2d2c47'
                                        }}
                                        onClick={() => performSort(column.label)}
                                    >
                                        {columnsFormatted[column.id]}
                                    </TableCell>
                                ))}
                            </TableHead>

                            {/* Render the table records */}
                            <TableBody>
                            {
                                

                                    recordsToRender
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

                                    // Render row-wise
                                    .map((row) => 
                                        (
                                            // Render each table record
                                            <TableRow hover role="checkbox" tabIndex={-1}>
                                            {
                                                // Render column-wise
                                                columns.map(column => {

                                                    let isAnArray = false // false -> str, true -> array
                                                    let value = row[column.id]
                                                    if (value && value[0] === '[' && value[value.length-1] === ']') {
                                                        value = JSON.parse(value)
                                                        isAnArray = true
                                                    }

                                                    // Enable links on 'Organization Id' and 'Organization Name' column fields
                                                    if (column.id === 'ent_name' || column.id === 'tmobile_ent_id') {

                                                        let queryArgs: KeyValueType = {
                                                            [column.id]: value
                                                        }

                                                        // Render the current cell
                                                        return (
                                                            
                                                            <TableCell 
                                                                key={column.id} 
                                                                align={column.align} 
                                                                style={{ minWidth: 120, maxWidth: 120, overflowX: 'auto' }}
                                                            >
                                                                <Link 
                                                                    to="#" 
                                                                    style={{ textDecoration: 'none'}} 
                                                                    onClick={() => handleLinkRecordClick(queryArgs)}
                                                                >
                                                                    {column.id === 'ent_name' ? value.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : value}
                                                                </Link>
                                                            </TableCell>                  
                                                        )
                                                    } 
                                                    
                                                    // Render plainly
                                                    else {

                                                        // Render the current cell
                                                        return (

                                                            <TableCell 
                                                                key={column.id} 
                                                                align={column.align} 
                                                                style={{ minWidth: 120, maxWidth: 120, overflowX: 'auto' }}
                                                            >
                                                                {
                                                                    // Render either array or string
                                                                    isAnArray ? (
                                                                        <div style={{ display: 'flex', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                                                        {
                                                                            value.length > 0 ? (
                                                                                value.map((item: any, index: any) => (
                                                                                <Fragment key={index}>
                                                                                    <div style={{ margin: '0 5px' }}>{item}</div>
                                                                                    {index !== value.length - 1 && <span>,</span>}
                                                                                </Fragment>
                                                                                ))
                                                                            ) : (
                                                                                '-'
                                                                            )
                                                                        }
                                                                        </div>
                                                                    ) : column.id === 'ent_name' ? value.split(' ').map((word: string) => word.charAt(0).toUpperCase()) : value
                                                                }
                                                            </TableCell>
                                                        )
                                                    }
                                                })
                                            }
                                            </TableRow>
                                        )
                                    )
                                
                            }
                            </TableBody>

                        </Table>

                    </TableContainer>

                    {/* Render pagination options */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={recordsToRender.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        showFirstButton
                        showLastButton
                    />
                    </>
                ) : (
                    showSkeleton ?
                    <Box sx={{ maxHeight: 440, maxWidth: open ? `calc(100vw - ${drawerWidth}px)` : `calc(150vw - ${drawerWidth}px)`}}>
                        <Box height={10}/>
                        <Skeleton variant="rectangular" animation="wave" width={"98%"} height={40}/>
                        <Box height={20}/>
                        <Skeleton variant="rectangular" animation="wave" width={"98%"} height={50}/>
                        <Box height={20}/>
                        <Skeleton variant="rectangular" animation="wave" width={"98%"} height={50}/>
                        <Box height={20}/>
                        <Skeleton variant="rectangular" animation="wave" width={"98%"} height={50}/>
                        <Box height={20}/>
                        <Skeleton variant="rectangular" animation="wave" width={"98%"} height={50}/>
                        <Box height={10}/>
                    </Box> :
                    <>
                        <Grid container justifyContent="center" alignItems="center">
                            <Grid item>
                                No data
                            </Grid>
                        </Grid>
                    </>
                )
            }
        </Box>
    )
}
 
export default TableOfRecords